/** Freshness: when the mirror was last read ("Read 9:35 AM"), in the top bar, marked fresh (the reference's online dot) or late (the alert icon). Its tooltip gives the mirror read and the last push; it links to Email, where the push lives. */
import { getSyncHealth } from "../../api/observability";
import { getSyncActivity } from "../../api/syncActivity";
import { fmtWhen, shortDate } from "../data/format";
import { useQuery } from "../data/useQuery";
import { Button, Graphic, Icon, Skeleton, Tooltip } from "../kit";
import { href } from "./links";

const POLL_MS = 60_000;

function when(iso: string | null | undefined, long = false): string | null {
  const full = fmtWhen(iso);
  if (!full || long || !full.includes(",")) return full;
  return shortDate(iso);
}

export function Freshness() {
  const health = useQuery("v2:sync-health", getSyncHealth, { intervalMs: POLL_MS });
  const push = useQuery("v2:last-push", () => getSyncActivity({ limit: 1 }), { intervalMs: POLL_MS });

  const lane = health.data?.lanes.find((l) => l.key === "members");
  const mirrorAt = when(lane?.last_success_at);
  const run = push.data?.summary.lastRun ?? null;
  // The time a push started, as Email and Settings write it.
  const pushAt = when(run?.startedAt ?? run?.finishedAt, true);
  const pushFailed = !!run && (run.errored > 0 || /fail|error/i.test(run.status));
  const stale = !!health.error || (!!lane && (lane.stale === true || !lane.ok));
  const late = stale || pushFailed;

  const mirrorLine = health.error ? "Mirror status did not load" : mirrorAt ? `Mirror read ${when(lane?.last_success_at, true)}${stale ? ", overdue" : ""}` : "Mirror not read yet";
  const pushLine = push.error ? "Push status did not load" : run ? `Last push ${pushAt}: ${run.errored ? `${run.errored} ${run.errored === 1 ? "error" : "errors"}` : "clean"}` : "No push yet";
  // Under failure the page's own LoadError is the one failure on show: the chip keeps only its alert icon, and the tooltip says what did not load.
  const chip = health.error ? null : mirrorAt ? `Read ${mirrorAt}` : "Not read yet";

  return (
    // A fixed footprint, the widest the chip reads ("Read 12:35 PM"), right-aligned: the top bar's buttons never move when it lands or its text changes.
    <span className="flex min-w-28 shrink-0 justify-end">
      {health.loading ? (
        <span className="flex h-8 w-full items-center">
          <Skeleton className="h-3 w-full" />
        </span>
      ) : (
        // To the left, inside the top bar's row: below, it would sit over the page heading's controls.
        <Tooltip
          side="left"
          content={
            <span className="flex flex-col gap-1.5">
              <span>{mirrorLine}</span>
              <span>{pushLine}</span>
            </span>
          }
        >
          <Button asChild variant="ghost" size="sm" className="-mr-2 tabular">
            <a href={href("/settings?open=activity")} data-state={late ? "late" : "fresh"} aria-label={chip ? undefined : mirrorLine}>
              {late ? <Icon name="feed-alert" size={14} /> : <Graphic name="online-dot" className="block size-1.5" />}
              {chip}
            </a>
          </Button>
        </Tooltip>
      )}
    </span>
  );
}
